import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/api/client";
import { Plus, Trash, GoogleLogo, FloppyDisk, LockKey, User as UserIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";

export default function Settings() {
  const { user } = useAuth();
  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto fade-in space-y-6" data-testid="settings-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">Workspace</div>
        <h1 className="font-display text-3xl font-bold text-neutral-900 mt-1">Settings</h1>
      </div>

      <ProfileCard />
      {user?.auth_provider !== "google" && <PasswordCard />}

      {user?.role === "admin" && <GoogleAuthRestrictions />}

      <div className="dos-card p-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-3">About Gray Owl Workspace</div>
        <p className="text-sm text-neutral-700 leading-relaxed">
          A unified operating system for project delivery and business management.
          A simple finance &amp; accounting workspace across every company you run — sales,
          expenses, invoices and reports, all in one clean place.
        </p>
        <div className="grid grid-cols-2 gap-2 mt-4 text-xs text-neutral-500">
          <div>Version: <span className="font-mono text-neutral-700">1.2.0</span></div>
          <div>Powered by: <span className="font-mono text-neutral-700">Claude 4.5 · GPT-5.2</span></div>
        </div>
      </div>
    </div>
  );
}

function ProfileCard() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    phone: "",
    designation: "",
    department: "",
    address: "",
    bio: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      email: user.email || "",
      mobile: user.mobile || "",
      phone: user.phone || "",
      designation: user.designation || "",
      department: user.department || "",
      address: user.address || "",
      bio: user.bio || "",
    });
  }, [user]);

  const isGoogle = user?.auth_provider === "google";

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (isGoogle) delete payload.email;
      await api.patch("/auth/me", payload);
      await refreshUser();
      toast.success("Profile updated");
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="dos-card p-6 space-y-4" data-testid="profile-card">
      <div className="flex items-center gap-2">
        <UserIcon size={18} weight="bold" className="text-neutral-700" />
        <div>
          <div className="font-display font-semibold text-neutral-900">My Profile</div>
          <div className="text-xs text-neutral-500">Update your personal details and contact information.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Full Name" required>
          <input data-testid="profile-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="dos-input" />
        </Field>
        <Field label={isGoogle ? "Email (Google-linked, locked)" : "Email"} required>
          <input data-testid="profile-email" type="email" required disabled={isGoogle} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="dos-input disabled:bg-neutral-50 disabled:text-neutral-500" />
        </Field>
        <Field label="Mobile">
          <input data-testid="profile-mobile" placeholder="+91 98765 43210" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="dos-input" />
        </Field>
        <Field label="Alternate Phone">
          <input data-testid="profile-phone" placeholder="Office / landline" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="dos-input" />
        </Field>
        <Field label="Designation">
          <input data-testid="profile-designation" placeholder="e.g. Project Manager" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="dos-input" />
        </Field>
        <Field label="Department">
          <input data-testid="profile-department" placeholder="e.g. Operations" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="dos-input" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Address">
            <input data-testid="profile-address" placeholder="Street, City, State, PIN" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="dos-input" />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Bio">
            <textarea data-testid="profile-bio" rows={2} placeholder="A short note about yourself" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="dos-input" />
          </Field>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <div className="text-xs text-neutral-500">Role: <span className="font-mono text-neutral-800">{user?.role}</span></div>
        <button data-testid="profile-save" disabled={saving} className="dos-btn dos-btn-primary">
          <FloppyDisk size={14} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
}

function PasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) { toast.error("Passwords do not match"); return; }
    if (form.new_password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.patch("/auth/me", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password changed");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="dos-card p-6 space-y-3" data-testid="password-card">
      <div className="flex items-center gap-2">
        <LockKey size={18} weight="bold" className="text-neutral-700" />
        <div>
          <div className="font-display font-semibold text-neutral-900">Change Password</div>
          <div className="text-xs text-neutral-500">Use at least 6 characters.</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Current Password">
          <input data-testid="pw-current" type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="dos-input" />
        </Field>
        <Field label="New Password">
          <input data-testid="pw-new" type="password" required value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="dos-input" />
        </Field>
        <Field label="Confirm New Password">
          <input data-testid="pw-confirm" type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="dos-input" />
        </Field>
      </div>
      <div className="flex justify-end">
        <button data-testid="pw-save" disabled={saving} className="dos-btn dos-btn-primary">
          <FloppyDisk size={14} /> {saving ? "Updating…" : "Update password"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function GoogleAuthRestrictions() {
  const [settings, setSettings] = useState({ google_allowed_domains: [], google_invite_only: false });
  const [invites, setInvites] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [newInvite, setNewInvite] = useState({ email: "", note: "" });
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  const load = async () => {
    try {
      const [w, i] = await Promise.all([api.get("/workspace/settings"), api.get("/workspace/google-invites")]);
      setSettings({
        google_allowed_domains: w.data.google_allowed_domains || [],
        google_invite_only: !!w.data.google_invite_only,
      });
      setInvites(i.data);
    } catch (err) { toast.error(formatError(err)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/workspace/settings", patch);
      setSettings({ google_allowed_domains: data.google_allowed_domains || [], google_invite_only: !!data.google_invite_only });
      toast.success("Saved");
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  const addDomain = () => {
    const d = newDomain.trim().toLowerCase().replace(/^@/, "");
    if (!d) return;
    if (settings.google_allowed_domains.includes(d)) { toast.error("Already added"); return; }
    save({ google_allowed_domains: [...settings.google_allowed_domains, d] });
    setNewDomain("");
  };
  const removeDomain = (d) => save({ google_allowed_domains: settings.google_allowed_domains.filter((x) => x !== d) });
  const toggleInvite = () => save({ google_invite_only: !settings.google_invite_only });

  const addInvite = async (e) => {
    e.preventDefault();
    try {
      await api.post("/workspace/google-invites", newInvite);
      setNewInvite({ email: "", note: "" });
      toast.success("Invite added");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const delInvite = async (inv) => {
    const ok = await confirm({ title: "Revoke invite?", message: `Remove invite for ${inv.email}?`, confirmLabel: "Revoke", tone: "danger" });
    if (!ok) return;
    try { await api.delete(`/workspace/google-invites/${inv.id}`); load(); toast.success("Revoked"); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="dos-card p-6" data-testid="google-restrictions">
      <div className="flex items-center gap-2 mb-4">
        <GoogleLogo size={18} weight="bold" className="text-neutral-700" />
        <div>
          <div className="font-display font-semibold text-neutral-900">Google Sign-In Restrictions</div>
          <div className="text-xs text-neutral-500">Control who can self-onboard through the Google button on the login page.</div>
        </div>
      </div>

      <div className="border-b border-neutral-100 pb-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-medium text-neutral-900">Domain allowlist</div>
            <div className="text-xs text-neutral-500">Only emails matching these domains can sign up. Leave empty to allow all.</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-2">
          {settings.google_allowed_domains.length === 0 && <span className="text-xs text-neutral-400">No restrictions — anyone can sign in with Google.</span>}
          {settings.google_allowed_domains.map((d) => (
            <span key={d} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-neutral-100 text-xs text-neutral-800 font-mono">
              @{d}
              <button data-testid={`rm-domain-${d}`} onClick={() => removeDomain(d)} className="text-neutral-400 hover:text-red-500"><Trash size={10} /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input data-testid="new-domain" placeholder="e.g. grayowl.com" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} className="dos-input" style={{ maxWidth: 260 }} />
          <button data-testid="add-domain" disabled={saving} onClick={addDomain} className="dos-btn dos-btn-secondary text-xs"><Plus size={12} /> Add domain</button>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-medium text-neutral-900">Invite-only mode</div>
            <div className="text-xs text-neutral-500">When ON, only emails listed below can sign in with Google.</div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input data-testid="toggle-invite-only" type="checkbox" checked={settings.google_invite_only} onChange={toggleInvite} className="sr-only peer" />
            <span className="w-10 h-5 rounded-full bg-neutral-200 peer-checked:bg-neutral-900 relative transition-colors">
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${settings.google_invite_only ? "translate-x-5" : "translate-x-0.5"}`}></span>
            </span>
          </label>
        </div>
        <form onSubmit={addInvite} className="flex flex-wrap gap-2 mt-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] uppercase tracking-wider text-neutral-500">Email to invite</label>
            <input data-testid="invite-email" required type="email" placeholder="partner@example.com" value={newInvite.email} onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })} className="dos-input" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-[10px] uppercase tracking-wider text-neutral-500">Note (optional)</label>
            <input data-testid="invite-note" placeholder="e.g. Consultant, Vendor rep" value={newInvite.note} onChange={(e) => setNewInvite({ ...newInvite, note: e.target.value })} className="dos-input" />
          </div>
          <button data-testid="add-invite" type="submit" className="dos-btn dos-btn-secondary text-xs"><Plus size={12} /> Add invite</button>
        </form>
        <div className="mt-3 space-y-1.5">
          {invites.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between text-sm py-1.5 border-b border-neutral-100 last:border-0">
              <div>
                <span className="font-mono text-neutral-800">{inv.email}</span>
                {inv.used && <span className="ml-2 dos-badge bg-emerald-50 text-emerald-700">Used</span>}
                {inv.note && <span className="ml-2 text-xs text-neutral-500">· {inv.note}</span>}
              </div>
              <button data-testid={`rm-invite-${inv.id}`} onClick={() => delInvite(inv)} className="p-1.5 rounded hover:bg-red-50 text-neutral-400 hover:text-red-500"><Trash size={12} /></button>
            </div>
          ))}
          {invites.length === 0 && <div className="text-xs text-neutral-400">No invites yet.</div>}
        </div>
      </div>
    </div>
  );
}
